import React from "react";
import {
  Eye,
  Edit2,
  Trash2,
  Building2,
  Mail,
  Phone,
  Globe,
  MapPin,
  Copy,
  Check,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

/**
 * Organizer Table Component
 * Displays organizers in table (desktop) or card (mobile) view
 */
const OrganizerTable = ({
  organizers,
  onView,
  onEdit,
  onDelete,
  copiedId,
  onCopy,
  currentPage,
  itemsPerPage,
  totalCount,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalCount);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const shortenSlug = (slug) => {
    if (!slug) return "";
    if (slug.length <= 12) return slug;
    // Show first 8 characters + "..." + last 4 characters
    return `${slug.substring(0, 8)}...${slug.substring(slug.length - 4)}`;
  };

  const getDefaultOrganizerLogo = (organizer) => {
    const initials = organizer.name
      .split(" ")
      .map((name) => name[0])
      .join("");
    return (
      <div className="flex overflow-hidden flex-shrink-0 justify-center items-center w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl shadow-md">
        {" "}
        <span className="text-xl font-medium text-white">{initials}</span>
      </div>
    );
  };

  if (organizers.length === 0) {
    return (
      <div className="overflow-hidden bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
        <div className="py-16 text-center">
          <div className="inline-flex justify-center items-center mb-4 w-16 h-16 bg-gray-100 rounded-full">
            <Building2 className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-gray-900">
            No organizers found
          </h3>
          <p className="mx-auto max-w-sm text-sm text-gray-600">
            Try adjusting your filters to find what you're looking for.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
      {/* Table Header */}
      <div className="px-4 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 sm:px-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-base font-bold text-gray-900 sm:text-lg">
              Organizers List
            </h3>
            <p className="text-sm text-gray-600 mt-0.5">
              {totalCount} {totalCount === 1 ? "organizer" : "organizers"} found
            </p>
          </div>
          <div className="hidden items-center space-x-2 text-sm text-gray-600 sm:flex">
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
            </select>
          </div>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden overflow-x-auto lg:block">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                Organizer Info
              </th>
              <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                Contact
              </th>
              <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                Location
              </th>
              <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                Created Date
              </th>
              <th className="px-6 py-4 text-xs font-bold tracking-wider text-right text-gray-700 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {organizers.map((organizer) => (
              <tr
                key={organizer.slug}
                className="transition-colors duration-150 hover:bg-blue-50/30"
              >
                {/* Organizer Info */}
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex overflow-hidden flex-shrink-0 justify-center items-center w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl shadow-md">
                      {organizer.logo ? (
                        <img
                          src={organizer.logo}
                          alt={organizer.name}
                          className="object-cover w-full h-full"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : null}
                      {!organizer.logo && getDefaultOrganizerLogo(organizer)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-gray-900 truncate">
                        {organizer.name}
                      </div>
                      <div className="flex items-center mt-1">
                        <button
                          onClick={() => onCopy(organizer.slug, "Organizer Slug")}
                          className="flex items-center font-mono text-xs text-blue-600 hover:text-blue-700 group"
                          title={organizer.slug}
                        >
                          <span className="bg-blue-50 px-2 py-0.5 rounded group-hover:bg-blue-100 transition-colors">
                            {shortenSlug(organizer.slug)}
                          </span>
                          {copiedId === organizer.slug ? (
                            <Check className="ml-1 w-3 h-3 text-green-600" />
                          ) : (
                            <Copy className="ml-1 w-3 h-3 opacity-0 transition-opacity group-hover:opacity-100" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </td>

                {/* Contact */}
                <td className="px-6 py-4">
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-900">
                      <Mail className="w-3.5 h-3.5 mr-2 text-gray-400 flex-shrink-0" />
                      <span className="truncate">{organizer.email}</span>
                    </div>
                    <button
                      onClick={() =>
                        onCopy(organizer.phone_number, "Phone number")
                      }
                      className="flex items-center text-sm text-gray-600 transition-colors hover:text-blue-600 group"
                    >
                      <Phone className="w-3.5 h-3.5 mr-2 text-gray-400 group-hover:text-blue-500 flex-shrink-0" />
                      <span className="truncate">{organizer.phone_number}</span>
                      {copiedId === organizer.phone_number ? (
                        <Check className="ml-1 w-3 h-3 text-green-600" />
                      ) : (
                        <Copy className="ml-1 w-3 h-3 opacity-0 transition-opacity group-hover:opacity-100" />
                      )}
                    </button>
                  </div>
                </td>

                {/* Location */}
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    <div className="flex items-center text-sm font-medium text-gray-900">
                      <Globe className="w-3.5 h-3.5 mr-2 text-gray-400" />
                      {organizer.country}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="w-3.5 h-3.5 mr-2 text-gray-400" />
                      {organizer.city}
                    </div>
                  </div>
                </td>

                {/* Created Date */}
                <td className="px-6 py-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-3.5 h-3.5 mr-2 text-gray-400 flex-shrink-0" />
                    <span>{formatDate(organizer.created_at)}</span>
                  </div>
                </td>

                {/* Actions */}
                <td className="px-6 py-4">
                  <div className="flex justify-end items-center space-x-1">
                    <button
                      onClick={() => onView(organizer)}
                      className="p-2 text-gray-400 rounded-lg transition-all hover:text-blue-600 hover:bg-blue-50"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onEdit(organizer)}
                      className="p-2 text-gray-400 rounded-lg transition-all hover:text-emerald-600 hover:bg-emerald-50"
                      title="Edit Organizer"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onDelete(organizer)}
                      className="p-2 text-gray-400 rounded-lg transition-all hover:text-red-600 hover:bg-red-50"
                      title="Delete Organizer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="divide-y divide-gray-100 lg:hidden">
        {organizers.map((organizer) => (
          <div
            key={organizer.slug}
            className="p-4 transition-colors hover:bg-blue-50/30"
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex flex-1 items-center space-x-3 min-w-0">
                <div className="flex overflow-hidden flex-shrink-0 justify-center items-center w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl shadow-md">
                  {organizer.logo ? (
                    <img
                      src={organizer.logo}
                      alt={organizer.name}
                      className="object-cover w-full h-full"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : null}
                  {!organizer.logo && getDefaultOrganizerLogo(organizer)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-base font-bold text-gray-900 truncate">
                    {organizer.name}
                  </div>
                  <button
                    onClick={() => onCopy(organizer.slug, "Organizer Slug")}
                    className="flex items-center mt-1 font-mono text-xs text-blue-600 hover:text-blue-700"
                    title={organizer.slug}
                  >
                    <span className="bg-blue-50 px-2 py-0.5 rounded">
                      {shortenSlug(organizer.slug)}
                    </span>
                    {copiedId === organizer.slug ? (
                      <Check className="ml-1 w-3 h-3 text-green-600" />
                    ) : (
                      <Copy className="ml-1 w-3 h-3" />
                    )}
                  </button>
                </div>
              </div>
              <div className="flex items-center text-xs text-gray-500">
                <Calendar className="w-3.5 h-3.5 mr-1" />
                {formatDate(organizer.created_at)}
              </div>
            </div>

            <div className="space-y-2.5 mb-3">
              <div className="flex items-center text-sm text-gray-900">
                <Mail className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                <span className="truncate">{organizer.email}</span>
              </div>
              <button
                onClick={() => onCopy(organizer.phone_number, "Phone number")}
                className="flex items-center w-full text-sm text-gray-600 transition-colors hover:text-blue-600"
              >
                <Phone className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                <span className="truncate">{organizer.phone_number}</span>
                {copiedId === organizer.phone_number ? (
                  <Check className="ml-auto w-3 h-3 text-green-600" />
                ) : (
                  <Copy className="ml-auto w-3 h-3" />
                )}
              </button>
              <div className="flex items-center text-sm text-gray-900">
                <Globe className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                <span>{organizer.country}</span>
                <span className="mx-2 text-gray-400">•</span>
                <MapPin className="flex-shrink-0 mr-1 w-4 h-4 text-gray-400" />
                <span>{organizer.city}</span>
              </div>
            </div>

            <div className="flex items-center pt-3 space-x-2 border-t border-gray-100">
              <button
                onClick={() => onView(organizer)}
                className="flex-1 flex items-center justify-center px-3 py-2.5 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors font-medium"
              >
                <Eye className="w-4 h-4 mr-1.5" />
                View
              </button>
              <button
                onClick={() => onEdit(organizer)}
                className="flex-1 flex items-center justify-center px-3 py-2.5 text-sm text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors font-medium"
              >
                <Edit2 className="w-4 h-4 mr-1.5" />
                Edit
              </button>
              <button
                onClick={() => onDelete(organizer)}
                className="flex items-center justify-center px-3 py-2.5 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-4 bg-gray-50 border-t border-gray-100 sm:px-6">
          <div className="flex flex-col justify-between items-center space-y-3 sm:flex-row sm:space-y-0">
            <div className="text-sm text-gray-600">
              Showing {startIndex} to {endIndex} of {totalCount} results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium bg-white rounded-lg border border-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`px-3 py-2 text-sm rounded-lg transition-colors font-medium ${
                      currentPage === pageNum
                        ? "bg-blue-600 text-white"
                        : "bg-white border border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() =>
                  onPageChange(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium bg-white rounded-lg border border-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizerTable;
